// Speech WebSocket protocol v1: the client half of BUILD 1 contract C-4.
//
// `src/speech/protocol.mjs` is the server half. The two cannot be one module:
// `ui/` is bundled by esbuild and `src/` is plain ESM. So this file mirrors it,
// and test/speech-protocol-contract-client.mjs compiles this file and asserts
// that every shared constant is equal and that frames encoded on either side
// decode identically on the other.
//
// Frozen after the b1-contracts tag (WP-0b). Changes go through WP-INT.
// BUILD 2 re-opens C-4 for voice sessions (C2-4, WP2-0b with WP2-0a's server
// half): `commit` and `keepalive` from the client, `endpoint` from the server,
// and optional fields on `ready`, `final` and `committed`. The binary frame and
// PROTOCOL_VERSION are unchanged; the additions are advertised by capability.

/** Binary frame, client → server. Little-endian, 12-byte header plus payload. */
export const PROTOCOL_VERSION = 1;
/** `kind` 1: PCM16LE, mono, 16 kHz. */
export const KIND_PCM16 = 1;
export const HEADER_BYTES = 12;
export const SAMPLE_RATE = 16000;
export const FRAME_MS = 100;
export const FRAME_SAMPLES = 1600;
/** A normal frame; only the last frame of a capture may be shorter. */
export const FRAME_BYTES = FRAME_SAMPLES * 2;
export const MIN_FRAME_BYTES = 2;
export const MAX_FRAME_BYTES = 32000;
/** Samples per millisecond: `startMs = sampleOffset / SAMPLES_PER_MS`. */
export const SAMPLES_PER_MS = SAMPLE_RATE / 1000;
export const U32_MAX = 0xffffffff;

export const FLAG_REPLAY = 1;
export const FLAG_FINAL_FRAME = 2;
const KNOWN_FLAGS = FLAG_REPLAY | FLAG_FINAL_FRAME;

/** The server acknowledges every `ACK_EVERY` frames. */
export const ACK_EVERY = 10;
/** Server heartbeat: a ping every 20 s; the socket is terminated after 40 s without a pong. */
export const HEARTBEAT_INTERVAL_MS = 20_000;
export const HEARTBEAT_TIMEOUT_MS = 40_000;
/** The server closes with 4408 after 30 s without frames. */
export const IDLE_CLOSE_MS = 30_000;
/** Mint tickets are single-use and expire after 60 s. */
export const TICKET_TTL_SEC = 60;

export const CLOSE_CODES = {
  stopped: 1000,
  internal: 1011,
  serverRestart: 1012,
  protocol: 4400,
  idle: 4408,
  tooLong: 4413,
  budget: 4429,
} as const;
export type SpeechCloseCode = (typeof CLOSE_CODES)[keyof typeof CLOSE_CODES];

/**
 * C-15 speech codes: WebSocket `error.code` and HTTP `error`. Codes only; the
 * copy lives in ui/i18n/ns/speech.ts.
 */
export const SPEECH_ERROR_CODES = [
  'rate_limited',
  'budget_exhausted',
  'provider_unavailable',
  'speech_unavailable',
  'invalid_request',
  'session_expired',
  'timeout',
  'too_long',
  'no_audio',
  'protocol',
  'internal',
  'not_authenticated',
  'request_origin_not_allowed',
  'company_assignment_required',
  'meeting_not_capturing',
  'payment_required',
] as const;
export type SpeechErrorCode = (typeof SPEECH_ERROR_CODES)[number];

export const CLOSED_REASONS = [
  'stopped',
  'cancelled',
  'idle',
  'max_duration',
  'server_restart',
  'replaced',
  'error',
] as const;
export type SpeechClosedReason = (typeof CLOSED_REASONS)[number];

export const CLIENT_MESSAGE_TYPES = ['stop', 'cancel', 'commit', 'keepalive'] as const;
export const SERVER_MESSAGE_TYPES = [
  'ready',
  'ack',
  'partial',
  'final',
  'committed',
  'resend',
  'degraded',
  'error',
  'closed',
  'endpoint',
] as const;

// Dictation client rules (C-4). Client-only; the server has no mirror.
/** Pause sending above this `bufferedAmount` and read from the spool later; never drop a frame. */
export const CLIENT_BACKPRESSURE_BYTES = 256 * 1024;
/** Replay pacing: at most 4× real time, so at least 25 ms per 100 ms frame. */
export const REPLAY_MIN_FRAME_INTERVAL_MS = 25;
/** Re-mint back-off after an unexpected close; the last step repeats. */
export const RECONNECT_BACKOFF_MS = [500, 1000, 2000, 4000, 8000] as const;
/** After 30 s without a new session the capture is `kept`. */
export const RECONNECT_WINDOW_MS = 30_000;
/** Wait at most 10 s for `committed` after `stop`; otherwise the capture is `kept`. */
export const COMMIT_TIMEOUT_MS = 10_000;
/**
 * Voice sessions send `keepalive` this often while voice mode is on (C2-4
 * "Idle"). The server's constant has the same name, so the interop test
 * compares the two.
 */
export const VOICE_KEEPALIVE_MS = 10_000;

export class ProtocolError extends Error {
  readonly code = 'protocol';
  constructor(message: string) {
    super(message);
    this.name = 'ProtocolError';
  }
}

export type FrameInput = {
  seq: number;
  sampleOffset: number;
  flags?: number;
  /** Int16LE mono 16 kHz samples. */
  pcm: ArrayBuffer | ArrayBufferView;
};
export type DecodedFrame = {
  version: number;
  kind: number;
  flags: number;
  seq: number;
  sampleOffset: number;
  /** A view into the decoded bytes; Int16LE samples. */
  pcm: Uint8Array;
};

const u32 = (value: number) => Number.isInteger(value) && value >= 0 && value <= U32_MAX;
const bytesOf = (data: ArrayBuffer | ArrayBufferView): Uint8Array =>
  data instanceof ArrayBuffer
    ? new Uint8Array(data)
    : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);

function assertPayload(byteLength: number) {
  if (byteLength < MIN_FRAME_BYTES || byteLength > MAX_FRAME_BYTES)
    throw new ProtocolError('frame payload out of bounds');
  if (byteLength % 2 !== 0) throw new ProtocolError('frame payload must be whole samples');
}

/** Encode one audio frame: the 12-byte header followed by the PCM payload. */
export function encodeFrame({ seq, sampleOffset, flags = 0, pcm }: FrameInput): Uint8Array {
  if (!u32(seq)) throw new ProtocolError('seq must be a u32');
  if (!u32(sampleOffset)) throw new ProtocolError('sampleOffset must be a u32');
  if (!Number.isInteger(flags) || (flags & ~KNOWN_FLAGS) !== 0)
    throw new ProtocolError('unknown frame flags');
  const payload = bytesOf(pcm);
  assertPayload(payload.byteLength);
  const frame = new Uint8Array(HEADER_BYTES + payload.byteLength);
  const header = new DataView(frame.buffer);
  header.setUint8(0, PROTOCOL_VERSION);
  header.setUint8(1, KIND_PCM16);
  header.setUint16(2, flags, true);
  header.setUint32(4, seq, true);
  header.setUint32(8, sampleOffset, true);
  frame.set(payload, HEADER_BYTES);
  return frame;
}

/** Decode one audio frame; throws `ProtocolError` on any violation of C-4. */
export function decodeFrame(data: ArrayBuffer | ArrayBufferView): DecodedFrame {
  const bytes = bytesOf(data);
  if (bytes.byteLength < HEADER_BYTES + MIN_FRAME_BYTES) throw new ProtocolError('frame too short');
  const header = new DataView(bytes.buffer, bytes.byteOffset, HEADER_BYTES);
  const version = header.getUint8(0);
  const kind = header.getUint8(1);
  const flags = header.getUint16(2, true);
  if (version !== PROTOCOL_VERSION) throw new ProtocolError('unsupported frame version');
  if (kind !== KIND_PCM16) throw new ProtocolError('unsupported frame kind');
  if ((flags & ~KNOWN_FLAGS) !== 0) throw new ProtocolError('unknown frame flags');
  assertPayload(bytes.byteLength - HEADER_BYTES);
  return {
    version,
    kind,
    flags,
    seq: header.getUint32(4, true),
    sampleOffset: header.getUint32(8, true),
    pcm: bytes.subarray(HEADER_BYTES),
  };
}

/** Capture-relative milliseconds of a sample offset (C-4 "Times"). */
export const sampleOffsetMs = (sampleOffset: number) => sampleOffset / SAMPLES_PER_MS;

/** The first `seq` to replay after a reconnect: `floor(lastFinalEndMs × 16 / 1600)`. */
export const replayFromSeq = (lastFinalEndMs: number) =>
  Math.max(0, Math.floor((lastFinalEndMs * SAMPLES_PER_MS) / FRAME_SAMPLES));

// Text messages (JSON). The server validates client messages with zod.

/**
 * `stop.lastSeq` is the last frame sent, or -1 when no frame was sent.
 * `commit` and `keepalive` are voice sessions only (C2-4); dictation and the
 * meeting preview answer them with a fatal `protocol` error. `commit`
 * finalises the turn up to `lastSeq` and keeps the session open.
 */
export type ClientControl =
  | { type: 'stop'; lastSeq: number }
  | { type: 'cancel' }
  | { type: 'commit'; turn: number; lastSeq: number }
  | { type: 'keepalive' };

export type SpeechLanguageMode = 'pinned' | 'set' | 'auto';
export type ReadyMessage = {
  type: 'ready';
  sessionId: string;
  provider: { id: string; model: string };
  languages: { mode: SpeechLanguageMode; codes: string[]; pin: string | null };
  partials: boolean;
  capabilities: {
    stable: 'word_final' | 'stabilized' | 'none';
    wordTimes: boolean;
    /** Voice sessions: false (or absent) from an older server means one session per turn. */
    commit?: boolean;
    /** Voice sessions: whether the provider reports ends of turn. */
    endpointing?: 'semantic' | 'none';
  };
  ackEvery: number;
};
export type AckMessage = { type: 'ack'; seq: number };
/** Replaces the text for `seg`. `stable` = leading characters that will not change. */
export type PartialMessage = {
  type: 'partial';
  seg: number | string;
  text: string;
  stable: number;
  startMs: number;
  endMs: number;
  speaker?: string;
};
/** Immutable. */
export type FinalMessage = {
  type: 'final';
  seg: number | string;
  text: string;
  startMs: number;
  endMs: number;
  lang?: string;
  speaker?: string;
  /** Voice sessions: the provider's end of turn (not a forced finalize). */
  endOfTurn?: boolean;
};
/**
 * Answers `stop`: this session's finals joined with single spaces. In a voice
 * session it also answers `commit`: `text` joins the finals since the previous
 * `committed`, and the session stays open.
 */
export type CommittedMessage = {
  type: 'committed';
  text: string;
  firstSeg: number | string | null;
  lastSeg: number | string | null;
  audioMs: number;
  provider: string;
  degraded: boolean;
  /** Voice sessions: the committed turn. */
  turn?: number;
  /** What ended the text; absent from an older server, meaning 'stop'. */
  cause?: 'stop' | 'commit';
  /** The provider's language for the turn: structural, steers internals, never shown. */
  lang?: string;
  /**
   * Voice sessions: true when this provider's end-of-turn detection is
   * verified for the turn's language. An endpoint commit auto-sends only then
   * (guard G2); otherwise it becomes a pending bubble. The client never holds
   * a language list.
   */
  endpointVerified?: boolean;
};
/**
 * Voice sessions in hands-free style only: the provider's end of turn. It is
 * advisory; the client decides whether to commit. `words` is the provider's
 * word count since the last commit.
 */
export type EndpointMessage = {
  type: 'endpoint';
  turn: number;
  seg: number | string;
  atMs: number;
  words: number;
};
export type ResendMessage = { type: 'resend'; fromSeq: number };
export type DegradedMessage = { type: 'degraded'; to: 'secondary' | 'utterance'; provider: string };
export type ErrorMessage = {
  type: 'error';
  code: SpeechErrorCode;
  retryAfterSec?: number;
  fatal: boolean;
};
export type ClosedMessage = { type: 'closed'; reason: SpeechClosedReason; audioMs: number };

export type ServerMessage =
  | ReadyMessage
  | AckMessage
  | PartialMessage
  | FinalMessage
  | CommittedMessage
  | ResendMessage
  | DegradedMessage
  | ErrorMessage
  | ClosedMessage
  | EndpointMessage;
export type ServerMessageType = ServerMessage['type'];

const serverTypes: ReadonlySet<string> = new Set(SERVER_MESSAGE_TYPES);

/**
 * Parse one server text message. Returns null for anything that is not a
 * JSON object with a known `type`; field validation belongs to the consumer.
 */
export function parseServerMessage(text: string): ServerMessage | null {
  try {
    const value: unknown = JSON.parse(text);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const type = (value as { type?: unknown }).type;
    return typeof type === 'string' && serverTypes.has(type) ? (value as ServerMessage) : null;
  } catch {
    return null;
  }
}

export const encodeControl = (control: ClientControl): string => JSON.stringify(control);
